import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ClassNoteLessonRepository } from '../api/repository/class-note-lesson';
import type { Id } from '../api/types';
import { EmptyState, Spinner } from '../components';
import { stripLeadingOrdinal } from '../config/content-types';
import { AlertTriangleIcon, BookOpenIcon } from '../layout/icons';
import { useQltAuthStore, useQltIztContentStore, useQltTimetableStore } from '../stores/qlt';
import { UnitList } from './panels/LectureLibraryTree';
import { classLabel, periodCell, resolvePeriodCourseScope } from './periodLabels';
import { readRecentLectures } from './recent';
import { periodSuggestions, taughtUnitIds, topicLessonName, type SuggestionSource } from './suggestions';
import type { BoardContextValue } from './useBoardContext';
import type { UnitSelection } from './useSelectUnit';
import { useLanguage } from '../i18n/msg';
import '../styles/submissions.css';

export interface BoardLectureSuggestionsProps {
  context: BoardContextValue;
  /** Loads the pick as this period's lecture. */
  onSelect: (selection: UnitSelection) => void;
}

function noteIdOf(note: { id?: Id; _id?: string } | null | undefined): Id | undefined {
  if (!note) return undefined;
  return note.id === undefined ? note._id : note.id;
}

function sourceLine(source: SuggestionSource | null, context: BoardContextValue, t: TFunction<'board'>): string {
  if (source === 'plan') return t('suggestions.plannedFor', { period: context.periodLabel || t('suggestions.thisPeriod') });
  if (source === 'next') return t('suggestions.afterLastTaught');
  return t('suggestions.fromLibrary', { class: classLabel(context.classTitle) });
}

// Đề xuất: one lesson for this period, derived on the board so a URL or reload still has it
export function BoardLectureSuggestions({ context, onSelect }: BoardLectureSuggestionsProps) {
  const { t } = useTranslation(['board', 'common']);
  const language = useLanguage();
  const tree = useQltIztContentStore((state) => state.tree);
  const loading = useQltIztContentStore((state) => state.loading);
  const error = useQltIztContentStore((state) => state.error);
  const loadTree = useQltIztContentStore((state) => state.loadTree);
  const teacherId = useQltAuthStore((state) => state.teacherId);
  const cells = useQltTimetableStore((state) => state.todayCells);
  const cell = periodCell(cells, context);
  // a board opened by URL has no route state, so the period's timetable cell names grade, subject and note
  const scope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);
  const noteId = context.classNoteId || noteIdOf(cell ? cell.classNote : null);
  const [linkedName, setLinkedName] = useState('');

  function reload() {
    void loadTree({ class: context.classId, grade: scope.gradeId, subject: scope.subjectId });
  }

  useEffect(() => {
    if (context.classId) void loadTree({ class: context.classId, grade: scope.gradeId, subject: scope.subjectId });
  }, [context.classId, scope.gradeId, scope.subjectId, loadTree]);

  // the teaching-plan lesson linked to this period's Sổ đầu bài, by name only
  useEffect(() => {
    setLinkedName('');
    if (noteId === undefined) return;
    let cancelled = false;
    ClassNoteLessonRepository.getLessonByClassNote(noteId)
      .then((lesson) => { if (!cancelled) setLinkedName(topicLessonName(lesson ? lesson.topicLesson : null)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [noteId]);

  const plannedName = linkedName || (cell && cell.classNote ? cell.classNote.lesson || '' : '');
  const result = useMemo(() => periodSuggestions(tree, {
    unitId: context.suggestedUnitId,
    name: context.suggestedUnitName,
    courseId: context.suggestedCourseId,
    plannedName,
    taughtUnitIds: taughtUnitIds(readRecentLectures({ teacherId }), context.classId, context.date),
  }), [tree, context.suggestedUnitId, context.suggestedUnitName, context.suggestedCourseId, plannedName, teacherId, context.classId, context.date, language]);

  if (loading && result.rows.length === 0) return <Spinner label={t('suggestions.loading')} size="small" />;
  if (error && result.rows.length === 0) return <EmptyState icon={<AlertTriangleIcon size={26} />} title={t('suggestions.loadFailed')} description={error} action={<button type="button" className="secondary-button" onClick={reload}>{t('common:actions.retry')}</button>} />;
  if (result.rows.length === 0) {
    const className = classLabel(context.classTitle);
    const description = context.subjectTitle
      ? t('suggestions.emptyWithSubject', { subject: context.subjectTitle, class: className })
      : t('suggestions.emptyNoSubject', { class: className });
    return <EmptyState icon={<BookOpenIcon size={26} />} title={t('suggestions.empty')} description={description} />;
  }

  return (
    <div className="lecture-library">
      <div className="hw-panel__bar"><p>{sourceLine(result.source, context, t)}</p></div>
      <UnitList rows={result.rows} activeUnitId={context.unitId} onSelect={(row) => onSelect({ unitId: row.unit.id, unitName: stripLeadingOrdinal(row.unit.name), courseId: row.courseId })} />
    </div>
  );
}
